import type { ComponentType } from "react";
import { createBrowserRouter, type RouteObject } from "react-router";

import { RedirectIfAuthenticated, RequireAuth, RequireRole, RootToLogin } from "@/components/auth/guards";
import { FullPageSkeleton } from "@/components/common/query-state";
import { AppShell } from "@/components/layout/app-shell";
import { RootLayout } from "@/components/layout/root-layout";
import { RouteErrorBoundary } from "@/components/layout/route-error-boundary";
import { CourseRoute, CoursesRoute, HomeRoute } from "@/pages/role-routes";

/** Lazy-load a page module whose default export is the page component. */
function page(load: () => Promise<{ default: ComponentType }>): Pick<RouteObject, "lazy"> {
  return { lazy: async () => ({ Component: (await load()).default }) };
}

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    hydrateFallbackElement: <FullPageSkeleton />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Legal pages stay public.
      { path: "/privacy", ...page(() => import("@/pages/marketing/PrivacyPage")) },
      { path: "/terms", ...page(() => import("@/pages/marketing/TermsPage")) },

      // "/login": the sign-in page for a signed-out visitor (it starts a silent sign-in unless they signed out or it
      // failed), the app for a signed-in one; "/" leads there, as the server's redirect does. The landing page
      // lives on humehr.app/learn.
      {
        element: <RedirectIfAuthenticated />,
        children: [
          { path: "/", element: <RootToLogin /> },
          { path: "/login", ...page(() => import("@/pages/auth/LoginPage")) },
        ],
      },

      // Invite links work for signed-in and anonymous visitors (who sign in with Hume ID first)
      { path: "/join/:code", ...page(() => import("@/pages/learner/JoinCoursePage")) },
      { path: "/invite/:token", ...page(() => import("@/pages/learner/AcceptInvitePage")) },
      { path: "/shared/:token", ...page(() => import("@/pages/guest/SharedItemPage")) },
      // Candidates invited by another Hume app: they sign in with Hume ID, bound to the invite (contract §10)
      { path: "/assess/:token", ...page(() => import("@/pages/assessments/AssessmentPage")) },

      // Anyone can check a certificate's code
      { path: "/verify/:code", ...page(() => import("@/pages/certificates/VerifyCertificatePage")) },

      // The application
      {
        path: "/app",
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <HomeRoute /> },
          { path: "courses", element: <CoursesRoute /> },
          { path: "courses/:courseId/certificate", ...page(() => import("@/pages/learner/CertificatePage")) },
          {
            path: "courses/:courseId",
            element: <CourseRoute />,
            children: [
              { index: true, ...page(() => import("@/pages/insights/CourseOverviewPage")) },
              { path: "assignments", ...page(() => import("@/pages/instructor/CourseAssignmentsPage")) },
              { path: "students", ...page(() => import("@/pages/instructor/CourseStudentsPage")) },
              { path: "students/:userId", ...page(() => import("@/pages/insights/StudentProfilePage")) },
              { path: "gradebook", ...page(() => import("@/pages/insights/GradebookPage")) },
              { path: "questions", ...page(() => import("@/pages/instructor/CourseQuestionsPage")) },
              { path: "settings", ...page(() => import("@/pages/instructor/CourseSettingsPage")) },
            ],
          },
          {
            element: <RequireRole role="instructor" />,
            children: [
              {
                path: "courses/:courseId/assignments/new",
                ...page(() => import("@/pages/instructor/AssignmentEditorPage")),
              },
              {
                path: "assignments/:assignmentId/edit",
                ...page(() => import("@/pages/instructor/AssignmentEditorPage")),
              },
              { path: "people", ...page(() => import("@/pages/instructor/PeoplePage")) },
              {
                path: "assessments/reports/:inviteId",
                ...page(() => import("@/pages/assessments/AssessmentReportPage")),
              },
              {
                path: "assignments/:assignmentId/analytics",
                ...page(() => import("@/pages/analytics/AssignmentAnalyticsPage")),
              },
              {
                path: "assignments/:assignmentId/analytics/students/:userId",
                ...page(() => import("@/pages/analytics/StudentAssignmentAnalyticsPage")),
              },
            ],
          },
          { path: "assignments/:assignmentId", ...page(() => import("@/pages/watch/WatchPage")) },
          { path: "settings", ...page(() => import("@/pages/settings/SettingsPage")) },
          { path: "*", ...page(() => import("@/pages/errors/NotFoundPage")) },
        ],
      },

      { path: "*", ...page(() => import("@/pages/errors/NotFoundPage")) },
    ],
  },
];

export const router = createBrowserRouter(routes);
